const cartDrawerTiers = {
  init() {
    this.injectBanner();
    this.initializeEventListeners();
    this.initializeMutationObserver();
    this.initModalEventListeners();
  },

  initializeEventListeners() {
    document.addEventListener('cart-drawer:open', this.injectBanner.bind(this));
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId === 'cart-drawer') {
        this.injectBanner();
      }
    });
  },

  initializeMutationObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }

    let isUpdating = false;
    this.observer = new MutationObserver((mutations) => {
      if (isUpdating) return;

      const hasRelevantChanges = mutations.some((mutation) => {
        if (mutation.target.classList?.contains('cart-countdown')) return false;
        return mutation.target.closest('cart-drawer-items') || mutation.target.querySelector('cart-drawer-items');
      });

      if (!hasRelevantChanges) return;

      isUpdating = true;
      const tierBanner = document.querySelector('#CartDrawerTiers');
      if (!tierBanner) {
        this.injectBanner();
      } else {
        this.updateProgress();
      }

      setTimeout(() => {
        isUpdating = false;
      }, 100);
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      this.observer.observe(cartDrawer, {
        childList: true,
        subtree: true,
        characterData: false,
      });
    }
  },

  getCurrencySymbol() {
    const currency = window.Shopify.currency?.active || 'EUR';
    const currencySymbols = {
      EUR: '€',
      CHF: 'CHF',
      USD: '$',
    };
    return currencySymbols[currency] || currency;
  },

  formatMoney(amount, asInteger = false) {
    const currency = this.getCurrencySymbol();
    const formattedAmount = asInteger ? Math.round(amount) : amount.toFixed(2);

    const preCurrencySymbols = ['$'];
    if (preCurrencySymbols.includes(currency)) {
      return `${currency}${formattedAmount}`;
    }
    return `${formattedAmount}${currency}`;
  },

  async getCurrentCartTotal() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const discountedTotal = cart.total_price / 100;
      const originalTotal =
        cart.items.reduce((sum, item) => {
          return sum + (item.original_line_price || item.line_price);
        }, 0) / 100;

      const hasDiscount = cart.total_discount > 0;
      const appliedDiscountCodes = cart.items.reduce((codes, item) => {
        if (item.line_level_discount_allocations?.length > 0) {
          item.line_level_discount_allocations.forEach((discount) => {
            const code = discount.discount_application?.title || '';
            if (code && !codes.includes(code)) {
              codes.push(code);
            }
          });
        }
        return codes;
      }, []);

      const appliedDiscountCode = appliedDiscountCodes.join(', ');

      const cartItems = cart.items.map((item) => {
        const originalLinePrice = item.original_line_price || item.line_price;
        const finalLinePrice = item.final_line_price || item.line_price;

        return {
          variant_id: item.variant_id,
          product_id: item.product_id,
          handle: item.handle,
          properties: item.properties || {},
          line_price: finalLinePrice / 100,
          original_line_price: originalLinePrice / 100,
          product_title: item.product_title,
        };
      });

      return {
        discountedTotal,
        originalTotal,
        hasDiscount,
        appliedDiscountCode,
        appliedDiscountCodes,
        cartItems,
      };
    } catch (error) {
      console.error('Error fetching cart:', error);
      return {
        discountedTotal: 0,
        originalTotal: 0,
        hasDiscount: false,
        appliedDiscountCode: '',
        appliedDiscountCodes: [],
        cartItems: [],
      };
    }
  },

  // Cache für Produkt-Collections
  productCollectionsCache: {},

  async getProductCollections(handle) {
    // Prüfe Cache
    if (this.productCollectionsCache[handle]) {
      return this.productCollectionsCache[handle];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      const productData = await response.json();

      // Collections werden über Tags ermittelt (da Shopify /products/.js keine Collections enthält)
      // Alternativ: prüfe ob Produkt bestimmte Collection-Tags hat
      const collections = productData.collections || [];

      // Cache speichern
      this.productCollectionsCache[handle] = {
        collections: collections,
        tags: productData.tags || [],
      };

      return this.productCollectionsCache[handle];
    } catch (error) {
      console.error(`Error fetching product collections for ${handle}:`, error);
      return { collections: [], tags: [] };
    }
  },

  parseExcludedCollections(excludedCollections) {
    if (!excludedCollections || !Array.isArray(excludedCollections)) return [];
    return excludedCollections
      .map((c) => (typeof c === 'string' ? c.trim().toLowerCase() : ''))
      .filter((c) => c.length > 0);
  },

  async calculateEligibleTotals(cartItems, excludedCollections) {
    const eligibleBaseItems = cartItems || [];

    if (!excludedCollections || excludedCollections.length === 0) {
      // Keine Ausschlüsse - kompletten Warenwert nutzen
      const discountedTotal = eligibleBaseItems.reduce((sum, item) => sum + item.line_price, 0);
      const originalTotal = eligibleBaseItems.reduce((sum, item) => sum + item.original_line_price, 0);
      return {
        eligibleDiscountedTotal: discountedTotal,
        eligibleOriginalTotal: originalTotal,
        excludedItems: [],
      };
    }

    const excludedItems = [];
    let eligibleDiscountedTotal = 0;
    let eligibleOriginalTotal = 0;

    // Prüfe jeden Artikel auf Collection-Zugehörigkeit
    for (const item of eligibleBaseItems) {
      // Collection-URL prüfen (aus cart.js item.url)
      const isExcluded = await this.isProductInExcludedCollection(item.handle, excludedCollections);

      if (isExcluded) {
        excludedItems.push(item);
      } else {
        eligibleDiscountedTotal += item.line_price;
        eligibleOriginalTotal += item.original_line_price;
      }
    }

    return {
      eligibleDiscountedTotal,
      eligibleOriginalTotal,
      excludedItems,
    };
  },

  // Cache für Collection-Produkte
  collectionProductsCache: {},
  collectionProductsCacheTime: {},
  CACHE_DURATION: 60000, // 1 Minute Cache

  async getCollectionProducts(collectionHandle) {
    const now = Date.now();

    // Prüfe ob Cache gültig ist
    if (
      this.collectionProductsCache[collectionHandle] &&
      this.collectionProductsCacheTime[collectionHandle] &&
      now - this.collectionProductsCacheTime[collectionHandle] < this.CACHE_DURATION
    ) {
      return this.collectionProductsCache[collectionHandle];
    }

    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250`);
      if (response.ok) {
        const data = await response.json();
        const productHandles = (data.products || []).map((p) => p.handle);

        // Cache speichern
        this.collectionProductsCache[collectionHandle] = productHandles;
        this.collectionProductsCacheTime[collectionHandle] = now;

        return productHandles;
      }
    } catch (error) {
      // Collection existiert möglicherweise nicht
      console.warn(`Collection '${collectionHandle}' nicht gefunden oder nicht zugänglich`);
    }

    return [];
  },

  async isProductInExcludedCollection(handle, excludedCollections) {
    // Prüfe via Collection-Zugehörigkeit API (mit Cache)
    for (const collectionHandle of excludedCollections) {
      const productHandles = await this.getCollectionProducts(collectionHandle);
      if (productHandles.includes(handle)) {
        return true;
      }
    }
    return false;
  },

  getData() {
    return window.cartDrawerTiersData || null;
  },

  async updateProgress() {
    const tierBanner = document.querySelector('#CartDrawerTiers');
    if (!tierBanner) return;

    const cartTotals = await this.getCurrentCartTotal();
    const data = this.getData();
    if (!data) return;

    // Parse globale ausgeschlossene Collections
    const globalExcludedCollections = this.parseExcludedCollections(data.globalExcludedCollections);

    // Berechne eligible totals (ohne globale ausgeschlossene Collections)
    const eligibleTotals = await this.calculateEligibleTotals(cartTotals.cartItems, globalExcludedCollections);

    // Per-Block eligible totals für Gift- und Collection-Choice-Tiers
    const perBlockTotals = {};

    if (Array.isArray(data.blocks)) {
      for (const block of data.blocks) {
        if (block.type === 'gift' || block.type === 'collection_choice') {
          const blockExcluded = this.parseExcludedCollections(block.settings?.excluded_collections);
          // Block-Einstellungen erweitern die globalen Ausschlüsse (Union)
          const combinedExcluded = Array.from(
            new Set([...(globalExcludedCollections || []), ...(blockExcluded || [])])
          );

          const blockEligibleTotals = await this.calculateEligibleTotals(cartTotals.cartItems, combinedExcluded);

          perBlockTotals[block.id] = {
            eligibleDiscountedTotal: blockEligibleTotals.eligibleDiscountedTotal,
            eligibleOriginalTotal: blockEligibleTotals.eligibleOriginalTotal,
            excludedItems: blockEligibleTotals.excludedItems,
            excludedCollections: combinedExcluded,
          };
        }
      }
    }

    // Erweitere cartTotals mit eligible values
    const extendedCartTotals = {
      ...cartTotals,
      eligibleDiscountedTotal: eligibleTotals.eligibleDiscountedTotal,
      eligibleOriginalTotal: eligibleTotals.eligibleOriginalTotal,
      excludedItems: eligibleTotals.excludedItems,
      globalExcludedCollections,
      perBlockTotals,
    };

    this.updateBannerUI(tierBanner, extendedCartTotals, data);
  },

  showMessage(message, type = 'warning') {
    const container = document.querySelector('.exclusion-message-container');
    if (!container) {
      console.warn('[cartDrawerTiers.showMessage] Exclusion message container not found');
      return;
    }

    const messageEl = container.querySelector('.exclusion-message');
    const messageText = container.querySelector('.message-text');

    if (!messageEl || !messageText) {
      console.warn('[cartDrawerTiers.showMessage] Message elements not found inside container');
      return;
    }

    // Styling je nach Nachrichtentyp
    const styles = {
      warning: {
        background: 'rgba(251, 140, 0, 0.1)',
        icon: '⚠️',
      },
      success: {
        background: 'rgba(76, 175, 80, 0.1)',
        icon: '✓',
      },
      error: {
        background: 'rgba(244, 67, 54, 0.1)',
        icon: '❌',
      },
    };

    const style = styles[type] || styles.warning;

    messageEl.style.background = style.background;
    messageText.innerHTML = `${style.icon} ${message}`;

    container.classList.add('show-exclusion-message');

    setTimeout(() => {
      container.classList.remove('show-exclusion-message');
    }, 5000);
  },

  updateBannerUI(banner, cartTotals, data) {
    if (!data?.blocks) return;

    const { blocks, settings } = data;
    const sortedBlocks = blocks.slice().sort((a, b) => a.settings.amount - b.settings.amount);
    if (!sortedBlocks.length) return;

    const highestTier = sortedBlocks[sortedBlocks.length - 1].settings.amount;
    const displayType = settings.display_type || 'timeline';

    // Verwende eligible totals (ohne globale ausgeschlossene Collections)
    const eligibleDiscountedTotal = cartTotals.eligibleDiscountedTotal ?? cartTotals.discountedTotal;
    const eligibleOriginalTotal = cartTotals.eligibleOriginalTotal ?? cartTotals.originalTotal;
    const perBlockTotals = cartTotals.perBlockTotals || {};

    // Progress-Berechnung mit eligible totals
    let progressPercentage;
    if (displayType === 'timeline') {
      progressPercentage = Math.min((eligibleDiscountedTotal / highestTier) * 100, 100);
    } else {
      const achievedTiers = sortedBlocks.filter((block) => {
        const relevantTotal = block.type === 'discount' ? eligibleOriginalTotal : eligibleDiscountedTotal;
        return relevantTotal >= block.settings.amount;
      }).length;
      progressPercentage = (achievedTiers / sortedBlocks.length) * 100;
    }

    // Update progress bar
    const progressFill = banner.querySelector('.progress-fill');
    if (progressFill) {
      progressFill.style.width = `${progressPercentage}%`;
    }

    // Update tier points
    let tierPoints = banner.querySelector('.tier-points');
    if (!tierPoints) {
      tierPoints = document.createElement('div');
      tierPoints.className = 'tier-points';
      banner.querySelector('.progress-bar').appendChild(tierPoints);
    }

    tierPoints.innerHTML = '';

    sortedBlocks.forEach((block, index) => {
      const position =
        displayType === 'timeline'
          ? (block.settings.amount / highestTier) * 100
          : (index / (sortedBlocks.length - 1)) * 100;

      const isDiscountTier = block.type === 'discount';
      const relevantTotal = isDiscountTier ? eligibleOriginalTotal : eligibleDiscountedTotal;

      const tierPoint = document.createElement('div');
      tierPoint.className = `tier-point ${relevantTotal >= block.settings.amount ? 'achieved' : ''}`;
      tierPoint.style.left = `${position}%`;
      tierPoint.style.cursor = 'pointer';

      tierPoint.innerHTML = `
      ${block.settings.icon}
      <div class="tier-amount">${settings.hide_prices ? '' : this.formatMoney(block.settings.amount, true)}</div>
    `;

      tierPoint.setAttribute('title', block.settings.benefit);
      tierPoint.dataset.blockIndex = index;
      tierPoints.appendChild(tierPoint);
    });

    const messageEl = banner.querySelector('.progress-message');
    if (!messageEl) return;

    if (settings.hide_cards) {
      messageEl.innerHTML = '';
      messageEl.style.display = 'none';
      return;
    }
    messageEl.style.display = '';

    // Find achieved and next tiers (using eligible totals)
    const achievedDiscountTiers = sortedBlocks.filter(
      (block) => block.type === 'discount' && eligibleOriginalTotal >= block.settings.amount
    );

    const achievedShippingTiers = sortedBlocks.filter(
      (block) => block.type === 'shipping' && eligibleDiscountedTotal >= block.settings.amount
    );

    const achievedGiftTiers = sortedBlocks.filter((block) => {
      if (block.type !== 'gift' && block.type !== 'collection_choice') return false;

      // Für Gift- & Collection-Choice-Tiers block-spezifische Exclusions berücksichtigen
      const blockTotals = perBlockTotals[block.id];
      const blockEligibleDiscountedTotal = blockTotals?.eligibleDiscountedTotal ?? eligibleDiscountedTotal;

      return blockEligibleDiscountedTotal >= block.settings.amount;
    });

    const nextTier = sortedBlocks.find((block) => {
      const isDiscountTier = block.type === 'discount';
      let relevantTotal;

      if (block.type === 'gift' || block.type === 'collection_choice') {
        const blockTotals = perBlockTotals[block.id];
        relevantTotal = blockTotals?.eligibleDiscountedTotal ?? eligibleDiscountedTotal;
      } else {
        relevantTotal = isDiscountTier ? eligibleOriginalTotal : eligibleDiscountedTotal;
      }

      return block.settings.amount > relevantTotal;
    });

    if (sortedBlocks.length > 0) {
      const allTiers = [
        ...(nextTier ? [nextTier] : []),
        ...achievedDiscountTiers.reverse(),
        ...achievedGiftTiers.reverse(),
        ...achievedShippingTiers.reverse(),
      ].sort((a, b) => {
        if (a === nextTier) return -1;
        if (b === nextTier) return 1;
        return b.settings.amount - a.settings.amount;
      });

      const desktopLayout = settings.desktop_layout || 'slider';
      const isSlider = window.innerWidth < 768 || desktopLayout === 'slider';

      const cardHTML = allTiers
        .map((tier) => {
          if (tier === nextTier) {
            // Für Next-Tier-Karte richtigen (ggf. block-spezifischen) Wert verwenden
            let tierRelevantTotal = tier.type === 'discount' ? eligibleOriginalTotal : eligibleDiscountedTotal;

            if (tier.type === 'gift' || tier.type === 'collection_choice') {
              const blockTotals = perBlockTotals[tier.id];
              tierRelevantTotal = blockTotals?.eligibleDiscountedTotal ?? eligibleDiscountedTotal;
            }

            const diff = tier.settings.amount - tierRelevantTotal;

            // Für Gift-/Collection-Tiers auch die block-spezifisch ausgeschlossenen Items an nextTierCard übergeben
            const blockTotals = perBlockTotals[tier.id];
            const excludedItemsForTier = blockTotals?.excludedItems ?? cartTotals.excludedItems;

            return cardTemplates.nextTierCard(tier, this.formatMoney(diff), tier.type, excludedItemsForTier);
          }

          switch (tier.type) {
            case 'shipping':
              return cardTemplates.shippingCard(tier);
            case 'discount':
              return cardTemplates.discountCard(tier, cartTotals);
            case 'gift':
              return cardTemplates.giftCard(tier, cartTotals);
            case 'collection_choice':
              return cardTemplates.collectionChoiceCard(tier, cartTotals);
            default:
              return '';
          }
        })
        .join('');

      messageEl.innerHTML = `
      <div class="tier-messages-slider" style="
        position: relative;
        overflow-x: ${isSlider ? 'auto' : 'visible'};
        scroll-snap-type: ${isSlider ? 'x mandatory' : 'none'};
        scrollbar-width: none;
        -ms-overflow-style: none;
        -webkit-overflow-scrolling: touch;
      ">
        <div class="tier-messages-container" style="
          display: flex;
          gap: 16px;
          padding: 4px 0;
        ">
          ${cardHTML}
        </div>
      </div>
      ${
        allTiers.length > 1
          ? `
        <div class="tier-slider-dots" style="
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 8px;
          position: sticky;
          bottom: 0;
          background: white;
          padding: 8px 0;
        ">
          ${Array(allTiers.length)
            .fill(0)
            .map(
              (_, index) => `
              <div class="tier-dot" 
                style="
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  background-color: #ddd;
                  cursor: pointer;
                  display: flex;
                "
                data-index="${index}"
              ></div>
            `
            )
            .join('')}
        </div>
      `
          : ''
      }
    `;

      // Initialize gift variants and slider functionality
      const giftContainers = banner.querySelectorAll('.gift-variants-container');
      giftContainers.forEach((container) => {
        const productId = container.dataset.productId;
        if (productId) {
          this.loadProductVariants(productId, container);
        }
      });

      // Initialize slider functionality
      const slider = messageEl.querySelector('.tier-messages-slider');
      const container = messageEl.querySelector('.tier-messages-container');
      const dots = messageEl.querySelectorAll('.tier-dot');

      if (slider && container && dots.length > 0 && isSlider) {
        const slideWidth = container.firstElementChild.offsetWidth + 16;

        const updateActiveDot = (activeIndex) => {
          dots.forEach((dot, index) => {
            dot.style.backgroundColor = index === activeIndex ? '#666' : '#ddd';
          });
        };

        slider.addEventListener('scroll', () => {
          const scrollPosition = slider.scrollLeft;
          const activeIndex = Math.round(scrollPosition / slideWidth);
          updateActiveDot(activeIndex);
        });

        dots.forEach((dot, index) => {
          dot.addEventListener('click', () => {
            const targetScroll = index * slideWidth;
            slider.scrollTo({
              left: targetScroll,
              behavior: 'smooth',
            });
          });
        });

        updateActiveDot(0);
      }

      // Add click handlers to tier points to scroll to corresponding card
      const tierPointsElements = banner.querySelectorAll('.tier-point');
      tierPointsElements.forEach((tierPoint) => {
        tierPoint.addEventListener('click', () => {
          const blockIndex = parseInt(tierPoint.dataset.blockIndex);
          const block = sortedBlocks[blockIndex];

          // Find the card index in allTiers array
          const cardIndex = allTiers.findIndex((tier) => tier === block);

          if (cardIndex !== -1) {
            const cards = container.querySelectorAll('.tier-message');
            const targetCard = cards[cardIndex];

            if (isSlider) {
              // Mobile/Slider: Scroll to card
              const slideWidth = container.firstElementChild.offsetWidth + 16;
              const targetScroll = cardIndex * slideWidth;
              slider.scrollTo({
                left: targetScroll,
                behavior: 'smooth',
              });
            } else if (targetCard) {
              // Desktop: Highlight card with green box-shadow (doesn't affect layout)
              targetCard.style.transition = 'box-shadow 0.3s ease-in-out';
              targetCard.style.boxShadow = '0 0 0 3px #16C60C';

              // Remove highlight after 800ms with ease out
              setTimeout(() => {
                targetCard.style.boxShadow = '';

                // Clean up transition after animation completes
                setTimeout(() => {
                  targetCard.style.transition = '';
                }, 300);
              }, 800);
            }
          }
        });
      });
    } else {
      messageEl.textContent = '✨ Sie haben alle Vorteile freigeschaltet!';
    }
  },

  async loadProductVariants(productId, container) {
    try {
      const response = await fetch(`/products/${productId}.js`);
      const productData = await response.json();
      const cartTotals = await this.getCurrentCartTotal();

      const select = container.querySelector('.gift-variant-select');
      const button = container.closest('.tier-message').querySelector('.tier-gift-button');
      const buttonContainer = container.closest('.tier-message').querySelector('.button-container');

      // Prüfen ob eine Variante des Produkts bereits im Warenkorb ist
      const isInCart = cartTotals.cartItems.some((item) =>
        productData.variants.some((variant) => variant.id === item.variant_id && item.properties._gift === 'true')
      );

      // Prüfen ob ein Gutschein verwendet wurde und ob dieser erlaubt ist
      const giftNote = container.dataset.giftNote || '';
      const allowedDiscountCodesRaw = container.dataset.allowedDiscountCodes || '';

      // Parse erlaubte Gutschein-Codes (kommasepariert, trimmen und case-insensitive)
      const allowedDiscountCodes = allowedDiscountCodesRaw
        .split(',')
        .map((code) => code.trim().toUpperCase())
        .filter((code) => code.length > 0);

      // Prüfen ob nicht-erlaubte Gutscheine verwendet wurden
      const hasBlockingDiscountCode =
        cartTotals.appliedDiscountCodes && cartTotals.appliedDiscountCodes.length > 0
          ? cartTotals.appliedDiscountCodes.some((code) => {
              const upperCode = code.toUpperCase();
              // Wenn keine erlaubten Codes definiert sind, blocken alle Codes
              // Wenn erlaubte Codes definiert sind, nur blocken wenn nicht in der Liste
              return allowedDiscountCodes.length === 0 || !allowedDiscountCodes.includes(upperCode);
            })
          : false;

      if (hasBlockingDiscountCode) {
        // Filtern nur die nicht-erlaubten Gutscheine für die Anzeige
        const blockingCodes = cartTotals.appliedDiscountCodes.filter((code) => {
          const upperCode = code.toUpperCase();
          return allowedDiscountCodes.length === 0 || !allowedDiscountCodes.includes(upperCode);
        });

        const discountCodesList = blockingCodes.map((code) => `'${code}'`).join(', ');
        const noteHtml = giftNote
          ? `
          <div style="margin-top: 8px; font-size: 0.85em; color: #666; line-height: 1.4;">
            ${giftNote}
          </div>
        `
          : '';

        buttonContainer.innerHTML = `
        <div style="padding: 8px; background: rgba(251, 140, 0, 0.1); border-radius: 4px; text-align: center; color: #f57c00; line-height: 1.4;">
          ⚠️ Nicht mit ${
            blockingCodes.length > 1 ? 'den Gutscheinen' : 'dem Gutschein'
          } ${discountCodesList} kombinierbar
        </div>
        ${noteHtml}
      `;
        select.style.display = 'none';
        return;
      }

      if (isInCart) {
        // Wenn das Geschenk bereits im Warenkorb ist
        const noteHtml = giftNote
          ? `
          <div style="margin-top: 8px; font-size: 0.85em; color: #666; line-height: 1.4;">
            ${giftNote}
          </div>
        `
          : '';

        buttonContainer.innerHTML = `
        <div style="padding: 8px; background: rgba(76, 175, 80, 0.1); border-radius: 4px; text-align: center; color: #4CAF50;">
          ✓ Geschenk ist bereits im Warenkorb
        </div>
        ${noteHtml}
      `;
        return;
      }

      // Wenn es nur eine Variante gibt
      if (productData.variants.length === 1) {
        button.disabled = false;
        button.onclick = () => this.addGiftToCart(productId, productData.variants[0].id);
        select.style.display = 'none';
        return;
      }

      // Mehrere Varianten: Select-Feld anzeigen und befüllen
      select.style.display = 'block';
      select.innerHTML =
        '<option value="">Bitte Variante wählen</option>' +
        productData.variants
          .map((variant) => {
            const availableForSale = variant.available ? '' : ' (ausverkauft)';
            const title = variant.title === 'Default Title' ? productData.title : variant.title;
            return `<option value="${variant.id}" ${
              !variant.available ? 'disabled' : ''
            }>${title}${availableForSale}</option>`;
          })
          .join('');

      // Event Listener für Select
      select.addEventListener('change', function () {
        button.disabled = !this.value;
      });
    } catch (error) {
      console.error('Error loading product variants:', error);
      container.innerHTML = '<div style="color: #f44336;">Fehler beim Laden der Produktvarianten</div>';
    }
  },

  async applyDiscount(code) {
    try {
      // Prüfen Sie zuerst den Warenkorb auf nicht zugelassene Artikel
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      // Angenommen, wir prüfen hier, ob Artikel nicht im Sale sind
      const nonSaleItems = await Promise.all(
        cart.items.map(async (item) => {
          // Hole Produkt-Details über die Shopify API
          const productResponse = await fetch(`/products/${item.handle}.js`);
          const productData = await productResponse.json();

          // Prüfe ob das Produkt den Tag "Rabatt" hat
          if (!productData.tags?.includes('Rabatt')) {
            return item;
          }
          return null;
        })
      ).then((items) => items.filter((item) => item !== null));

      if (nonSaleItems.length > 0) {
        const itemsList = nonSaleItems.map((item) => `• ${item.product_title}`).join('<br>');
        this.showMessage(
          `
            Bitte beachte, dass bestimmte Artikel in Deinem Warenkorb nicht an der Rabatt-Aktion teilnehmen:<br>
            <div style="margin-top: 8px; margin-left: 8px;">
              ${itemsList}
            </div>
          `,
          'warning'
        );
        return;
      }

      const response = await fetch('/discount/' + code);
      if (!response.ok) {
        throw new Error('Failed to apply discount');
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Kompletten Cart Drawer aktualisieren
        await fetch('/?section_id=cart-drawer')
          .then((response) => response.text())
          .then((html) => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const newDrawer = tempDiv.querySelector('cart-drawer');
            if (newDrawer) {
              cartDrawer.innerHTML = newDrawer.innerHTML;
            }
          });

        // Banner aktualisieren
        this.updateProgress();

        this.showMessage('Rabatt wurde erfolgreich aktiviert!', 'success');
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      this.showMessage(
        error.message === 'Failed to apply discount'
          ? 'Gutschein konnte nicht aktiviert werden'
          : 'Fehler beim Aktivieren des Rabatts',
        'error'
      );
    }
  },

  async addGiftToCart(productId, variantId) {
    if (!productId || !variantId) {
      this.showMessage('Bitte wähle eine Variante aus', 'warning');
      return;
    }

    try {
      // Zum Warenkorb hinzufügen
      const formData = {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _gift: 'true',
            },
          },
        ],
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Hinzufügen des Geschenks');
      }

      // Cart Drawer aktualisieren
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        await fetch('/?section_id=cart-drawer')
          .then((response) => response.text())
          .then((html) => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const newDrawer = tempDiv.querySelector('cart-drawer');
            if (newDrawer) {
              cartDrawer.innerHTML = newDrawer.innerHTML;
            }
          });

        // Banner aktualisieren
        this.updateProgress();
        this.showMessage('Geschenk wurde erfolgreich hinzugefügt!', 'success');
      }
    } catch (error) {
      console.error('Error adding gift to cart:', error);
      this.showMessage('Fehler beim Hinzufügen des Geschenks', 'error');
    }
  },

  openCollectionModal(collectionHandle, modalTitle) {
    let modal = document.getElementById('collectionChoiceModal');

    // Wenn kein Modal existiert, dynamisch erzeugen
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'collectionChoiceModal';
      modal.className = 'collection-modal-overlay';
      modal.innerHTML = `
        <div class="collection-modal-sheet">
          <div class="collection-modal-header">
            <h3 class="collection-modal-title"></h3>
            <button class="collection-modal-close" type="button">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" style="width: 24px; height: 24px;">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="collection-modal-content">
            <div class="collection-products-grid"></div>
          </div>
        </div>
      `;
      document.body.appendChild(modal);

      // Close-Events initialisieren
      this.initModalEventListeners();

      // Close-Button Click-Handler binden (falls initModalEventListeners in anderem Kontext aufgerufen wurde)
      const closeBtn = modal.querySelector('.collection-modal-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeCollectionModal());
      }
    }

    const titleEl = modal.querySelector('.collection-modal-title');
    const contentEl = modal.querySelector('.collection-products-grid');

    if (!titleEl || !contentEl) {
      console.warn('[cartDrawerTiers.openCollectionModal] Modal content elements not found');
      return;
    }

    titleEl.textContent = modalTitle || 'Wähle deinen Artikel';
    contentEl.innerHTML = '<div class="collection-modal-loading">Lade Produkte...</div>';

    modal.classList.add('show');
    document.body.style.overflow = 'hidden';

    this.loadCollectionProducts(collectionHandle, contentEl);
  },

  closeCollectionModal() {
    const modal = document.getElementById('collectionChoiceModal');
    if (modal) {
      modal.classList.remove('show');
      document.body.style.overflow = '';
    }
  },

  initModalEventListeners() {
    const modal = document.getElementById('collectionChoiceModal');
    if (!modal) return;

    // Damit wir Events nicht doppelt registrieren
    if (this._modalEventsInitialized) return;
    this._modalEventsInitialized = true;

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeCollectionModal();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('show')) {
        this.closeCollectionModal();
      }
    });
  },

  async loadCollectionProducts(collectionHandle, containerEl) {
    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=50`);
      if (!response.ok) throw new Error('Fehler beim Laden der Produkte');

      const data = await response.json();
      const products = data.products || [];

      if (products.length === 0) {
        containerEl.innerHTML = '<div class="collection-modal-loading">Keine Produkte gefunden</div>';
        return;
      }

      // Produkte rendern
      containerEl.innerHTML = products
        .map((product, index) => {
          const variants = product.variants || [];
          const hasMultipleVariants = variants.length > 1;
          const firstVariant = variants[0] || {};
          const firstAvailableVariant = variants.find((v) => v.available) || firstVariant;
          const allOutOfStock = variants.every((v) => !v.available);

          // Initial: Bild der ersten verfügbaren Variante oder Featured Image
          let initialImage = '';

          // Versuche zuerst das Varianten-Bild zu holen
          if (firstAvailableVariant.featured_image?.src) {
            initialImage = firstAvailableVariant.featured_image.src;
          } else if (firstAvailableVariant.image_id && product.images) {
            const matchingImage = product.images.find((img) => img.id === firstAvailableVariant.image_id);
            if (matchingImage?.src) {
              initialImage = matchingImage.src;
            }
          }

          // Fallback auf Produkt-Bild
          if (!initialImage) {
            if (product.images?.[0]) {
              const imageObj = product.images[0];
              if (typeof imageObj === 'string') {
                initialImage = imageObj;
              } else if (imageObj?.src) {
                initialImage = imageObj.src;
              }
            } else if (product.image) {
              initialImage = product.image;
            } else if (product.featured_image) {
              initialImage = product.featured_image;
            }
          }

          // Varianten-Select HTML
          const variantSelectHtml = hasMultipleVariants
            ? `
              <select 
                class="collection-product-variant-select" 
                data-product-index="${index}"
                onchange="cartDrawerTiers.updateVariantSelection(${index})"
              >
                ${variants
                  .map((variant, vIndex) => {
                    const title = variant.title === 'Default Title' ? product.title : variant.title;
                    const availableText = variant.available ? '' : ' (Ausverkauft)';
                    // Wähle die erste verfügbare Variante als Standard
                    const isFirstAvailable = variant.available && !variants.slice(0, vIndex).some((v) => v.available);

                    // Varianten-Bild URL
                    let variantImage = '';
                    if (variant.featured_image?.src) {
                      variantImage = variant.featured_image.src;
                    } else if (variant.image_id && product.images) {
                      const matchingImage = product.images.find((img) => img.id === variant.image_id);
                      if (matchingImage?.src) {
                        variantImage = matchingImage.src;
                      }
                    }

                    // Fallback auf Produkt-Bild falls Variante kein eigenes Bild hat
                    if (!variantImage) {
                      if (product.images?.[0]?.src) {
                        variantImage = product.images[0].src;
                      } else if (product.image) {
                        variantImage = product.image;
                      } else if (product.featured_image) {
                        variantImage = product.featured_image;
                      }
                    }

                    return `
                      <option 
                        value="${variant.id}" 
                        data-available="${variant.available}"
                        data-image="${variantImage}"
                        ${!variant.available ? 'disabled' : ''}
                        ${isFirstAvailable ? 'selected' : ''}
                      >
                        ${title}${availableText}
                      </option>
                    `;
                  })
                  .join('')}
              </select>
            `
            : '';

          const imageHtml = initialImage
            ? `<img 
                src="${initialImage}" 
                alt="${product.title}" 
                class="collection-product-image"
                data-product-index="${index}"
                loading="lazy"
                onerror="this.style.display='none'"
              />`
            : `<div class="collection-product-image" data-product-index="${index}" style="display: flex; align-items: center; justify-content: center; background: #f0f0f0; color: #999;">
                Kein Bild
              </div>`;

          return `
            <div class="collection-product-card" data-product-index="${index}">
              ${imageHtml}
              <div class="collection-product-title">
                ${product.title}
              </div>
              ${variantSelectHtml}
              <button 
                class="collection-product-add-btn"
                data-product-index="${index}"
                data-variant-id="${!hasMultipleVariants ? firstAvailableVariant.id : ''}"
                data-collection-handle="${collectionHandle}"
                onclick="cartDrawerTiers.addCollectionProductToCart('${product.handle}', this)"
                ${allOutOfStock ? 'disabled' : ''}
              >
                ${allOutOfStock ? 'Ausverkauft' : 'Hinzufügen'}
              </button>
            </div>
          `;
        })
        .join('');
    } catch (error) {
      console.error('Error loading collection products:', error);
      containerEl.innerHTML = '<div class="collection-modal-loading">Fehler beim Laden der Produkte</div>';
    }
  },

  updateVariantSelection(productIndex) {
    const card = document.querySelector(`.collection-product-card[data-product-index="${productIndex}"]`);
    if (!card) return;

    const select = card.querySelector('.collection-product-variant-select');
    const button = card.querySelector('.collection-product-add-btn');
    const image = card.querySelector('.collection-product-image');

    if (!select || !button) return;

    const selectedOption = select.options[select.selectedIndex];
    const isAvailable = selectedOption.getAttribute('data-available') === 'true';
    const variantImage = selectedOption.getAttribute('data-image');

    // Update Button
    button.disabled = !isAvailable;
    button.textContent = isAvailable ? 'Hinzufügen' : 'Ausverkauft';

    // Update Image mit Transition
    if (image && variantImage) {
      if (image.tagName === 'IMG') {
        // Fade-out
        image.classList.add('changing');

        // Ändere das Bild nach kurzer Verzögerung
        setTimeout(() => {
          image.src = variantImage;
          image.onload = () => {
            image.classList.remove('changing');
          };
        }, 100);
      } else {
        // Falls es ein Platzhalter-Div ist, ersetze es mit einem Image
        const newImage = document.createElement('img');
        newImage.src = variantImage;
        newImage.alt = selectedOption.textContent;
        newImage.className = 'collection-product-image';
        newImage.setAttribute('data-product-index', productIndex);
        newImage.loading = 'lazy';
        newImage.onerror = function () {
          this.style.display = 'none';
        };
        image.replaceWith(newImage);
      }
    }
  },

  async addCollectionProductToCart(productHandle, buttonElement) {
    try {
      const card = buttonElement.closest('.collection-product-card');
      if (!card) return;

      // Prüfe ob es einen Varianten-Select gibt
      const variantSelect = card.querySelector('.collection-product-variant-select');
      let variantId;

      if (variantSelect) {
        // Mehrere Varianten: Hole ausgewählte Variante
        variantId = variantSelect.value;
      } else {
        // Einzelne Variante: Hole aus data-Attribut des Buttons
        variantId = buttonElement.getAttribute('data-variant-id');
      }

      if (!variantId) {
        this.showMessage('Bitte wähle eine Variante aus', 'warning');
        return;
      }

      // Collection-Handle für eindeutige Zuordnung im Warenkorb mitgeben
      const collectionHandle = buttonElement.getAttribute('data-collection-handle') || '';

      const formData = {
        items: [
          {
            id: parseInt(variantId),
            quantity: 1,
            properties: {
              _gift: 'true',
              ...(collectionHandle ? { _gift_collection: collectionHandle } : {}),
            },
          },
        ],
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Hinzufügen');
      }

      // Modal schließen
      this.closeCollectionModal();

      // Cart Drawer aktualisieren
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        await fetch('/?section_id=cart-drawer')
          .then((response) => response.text())
          .then((html) => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const newDrawer = tempDiv.querySelector('cart-drawer');
            if (newDrawer) {
              cartDrawer.innerHTML = newDrawer.innerHTML;
            }
          });

        this.updateProgress();
        this.showMessage('Artikel wurde erfolgreich hinzugefügt!', 'success');
      }
    } catch (error) {
      console.error('Error adding product to cart:', error);
      this.showMessage('Fehler beim Hinzufügen des Artikels', 'error');
    }
  },

  async injectBanner() {
    const cartDrawer = document.querySelector('cart-drawer cart-drawer-items');
    if (!cartDrawer) return;

    try {
      const response = await fetch(`${window.Shopify.routes.root}?sections=heideman-cart-drawer-tiers`);
      const sections = await response.json();
      const html = sections['heideman-cart-drawer-tiers'];

      if (!html) return;

      const existingScript = document.getElementById('CartDrawerTiersData');
      const existingBanner = document.getElementById('CartDrawerTiers');
      if (existingScript) existingScript.remove();
      if (existingBanner) existingBanner.remove();

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const newScript = tempDiv.querySelector('script');
      if (newScript) {
        document.body.appendChild(newScript);
      }

      const sectionContent = tempDiv.querySelector('.cart-drawer-tiers');
      if (sectionContent) {
        cartDrawer.parentNode.insertBefore(sectionContent, cartDrawer);
        requestAnimationFrame(() => {
          sectionContent.classList.add('show');
          this.updateProgress();
        });
      }
    } catch (error) {
      console.error('Error loading tier banner:', error);
    }
  },
};

// Neue Helper-Funktionen für die Card-Templates
const cardTemplates = {
  createBaseCard(content, backgroundColor = 'rgba(76, 175, 80, 0.1)') {
    return `
      <div class="tier-message" style="
        flex: 0 0 auto;
        scroll-snap-align: start;
        padding: 12px;
        border-radius: 8px;
        min-width: 280px;
        max-width: 70vw;
        position: relative;
        background: ${backgroundColor};
      ">
        ${content}
      </div>
    `;
  },

  nextTierCard(tier, formattedAmount, type, excludedItems = []) {
    let exclusionNote = '';

    if (excludedItems && excludedItems.length > 0) {
      const itemsList = excludedItems.map((item) => `• ${item.product_title}`).join('<br>');

      exclusionNote = `
        <div style="margin-top: 12px; padding: 8px; background: rgba(251, 140, 0, 0.1); border-radius: 4px; font-size: 0.85em; color: #f57c00; line-height: 1.4;">
          <div style="font-weight: 500; margin-bottom: 4px;">
            ⚠️ ${excludedItems.length === 1 ? 'Dieser Artikel zählt' : 'Diese Artikel zählen'} nicht für diese Aktion:
          </div>
          <div style="margin-left: 4px; font-size: 0.95em;">
            ${itemsList}
          </div>
        </div>
      `;
    }

    return this.createBaseCard(
      `
      <div style="margin-bottom: 8px;">${tier.settings.benefit} bald erreicht!</div>
      <div style="font-size: 0.9em; color: #666;">
        Noch ${formattedAmount}
        bis ${type === 'shipping' ? 'zum kostenlosen Versand' : type === 'gift' ? 'zum Gratis Artikel' : 'zum Rabatt'}
      </div>
      ${exclusionNote}
    `,
      'rgba(128, 128, 128, 0.1)'
    );
  },

  shippingCard(tier) {
    return this.createBaseCard(`
      <div>${tier.settings.icon} ${tier.settings.benefit} erreicht!</div>
      <div style="margin-top: 8px; font-size: 0.9em; color: #666;">
        Wird automatisch im Checkout berücksichtigt
      </div>
    `);
  },

  discountCard(tier, cartTotals) {
    const isApplied = cartTotals.appliedDiscountCodes.some((code) => code.includes(tier.settings.discount_code));

    const actionButton = isApplied
      ? `<div style="padding: 8px; background: rgba(76, 175, 80, 0.1); border-radius: 4px; text-align: center; color: #4CAF50;">
          ✓ Rabatt ist aktiv
        </div>`
      : `<button 
          class="tier-discount-button" 
          onclick="cartDrawerTiers.applyDiscount('${tier.settings.discount_code}')"
          style="width: 100%; padding: 8px 16px; background-color: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer;"
        >
          Rabatt aktivieren
        </button>`;

    return this.createBaseCard(`
      <div style="margin-bottom: 8px; display: flex; align-items: center; justify-content: center; gap: 8px;">
        <span>${tier.settings.icon} ${tier.settings.benefit} verfügbar!</span>
        ${this.createInfoIcon()}
      </div>
      ${actionButton}
    `);
  },

  giftCard(tier, cartTotals = {}) {
    const noteHtml = tier.settings.gift_note
      ? `
      <div style="margin-top: 8px; font-size: 0.85em; color: #666; line-height: 1.4;">
        ${tier.settings.gift_note}
      </div>
    `
      : '';

    const excludedCollectionsJson = JSON.stringify(tier.settings.excluded_collections || []);

    return this.createBaseCard(`
      <div style="margin-bottom: 8px;">${tier.settings.icon} ${tier.settings.benefit} verfügbar!</div>
      <div class="gift-variants-container" 
        data-product-id="${tier.settings.gift_product}" 
        data-single-variant="false" 
        data-gift-note="${tier.settings.gift_note || ''}"
        data-allowed-discount-codes="${tier.settings.allowed_discount_codes || ''}"
        data-excluded-collections='${excludedCollectionsJson}'>
        <select class="gift-variant-select" style="
          display: none;
          width: 100%;
          padding: 8px;
          margin-bottom: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
        ">
          <option value="">Bitte Variante wählen</option>
        </select>
        <div class="button-container">
          <button 
            class="tier-gift-button" 
            onclick="cartDrawerTiers.addGiftToCart('${
              tier.settings.gift_product
            }', this.closest('.tier-message').querySelector('.gift-variant-select').value)"
            style="width: 100%; padding: 8px 16px; background-color: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer;"
            disabled
          >
            Gratis Artikel hinzufügen
          </button>
          ${noteHtml}
        </div>
      </div>
    `);
  },

  collectionChoiceCard(tier, cartTotals = {}) {
    const noteHtml = tier.settings.gift_note
      ? `
      <div style="margin-top: 8px; font-size: 0.85em; color: #666; line-height: 1.4;">
        ${tier.settings.gift_note}
      </div>
    `
      : '';

    const buttonText = tier.settings.button_text || 'Artikel auswählen';
    const modalTitle = tier.settings.modal_title || 'Wähle deinen Gratis-Artikel';
    const collectionHandle = tier.settings.collection || '';

    if (!collectionHandle) {
      return this.createBaseCard(`
        <div style="margin-bottom: 8px;">${tier.settings.icon} ${tier.settings.benefit} verfügbar!</div>
        <div style="padding: 8px; background: rgba(244, 67, 54, 0.1); border-radius: 4px; text-align: center; color: #f44336;">
          ⚠️ Keine Collection ausgewählt
        </div>
      `);
    }

    // Prüfen ob ein Gutschein verwendet wurde und ob dieser erlaubt ist
    const allowedDiscountCodesRaw = tier.settings.allowed_discount_codes || '';
    const allowedDiscountCodes = allowedDiscountCodesRaw
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter((code) => code.length > 0);

    const hasBlockingDiscountCode =
      cartTotals.appliedDiscountCodes && cartTotals.appliedDiscountCodes.length > 0
        ? cartTotals.appliedDiscountCodes.some((code) => {
            const upperCode = code.toUpperCase();
            return allowedDiscountCodes.length === 0 || !allowedDiscountCodes.includes(upperCode);
          })
        : false;

    if (hasBlockingDiscountCode) {
      const blockingCodes = cartTotals.appliedDiscountCodes.filter((code) => {
        const upperCode = code.toUpperCase();
        return allowedDiscountCodes.length === 0 || !allowedDiscountCodes.includes(upperCode);
      });
      const discountCodesList = blockingCodes.map((code) => `'${code}'`).join(', ');

      return this.createBaseCard(`
        <div style="margin-bottom: 8px;">${tier.settings.icon} ${tier.settings.benefit} verfügbar!</div>
        <div style="padding: 8px; background: rgba(251, 140, 0, 0.1); border-radius: 4px; text-align: center; color: #f57c00; line-height: 1.4;">
          ⚠️ Nicht mit ${
            blockingCodes.length > 1 ? 'den Gutscheinen' : 'dem Gutschein'
          } ${discountCodesList} kombinierbar
        </div>
        ${noteHtml}
      `);
    }

    // Prüfen ob bereits ein Gift aus DIESER Collection im Warenkorb ist
    const hasGiftInCart = cartTotals.cartItems?.some(
      (item) =>
        item.properties && item.properties._gift === 'true' && item.properties._gift_collection === collectionHandle
    );

    if (hasGiftInCart) {
      return this.createBaseCard(`
        <div style="margin-bottom: 8px;">${tier.settings.icon} ${tier.settings.benefit} verfügbar!</div>
        <div style="padding: 8px; background: rgba(76, 175, 80, 0.1); border-radius: 4px; text-align: center; color: #4CAF50;">
          ✓ Geschenk ist bereits im Warenkorb
        </div>
        ${noteHtml}
      `);
    }

    return this.createBaseCard(`
      <div style="margin-bottom: 8px;">${tier.settings.icon} ${tier.settings.benefit} verfügbar!</div>
      <button 
        class="tier-collection-choice-button" 
        onclick="cartDrawerTiers.openCollectionModal('${collectionHandle}', '${modalTitle}')"
        style="width: 100%; padding: 8px 16px; background-color: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer;"
      >
        ${buttonText}
      </button>
      ${noteHtml}
    `);
  },

  createInfoIcon() {
    return `
      <div class="info-icon" style="
        cursor: pointer;
        position: relative;
        display: inline-flex;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 1.5em; height: 1.5em;">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
        <div class="tooltip" style="
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          padding: 8px;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          width: 200px;
          display: none;
          z-index: 10;
          margin-top: 8px;
          font-size: 0.9em;
          color: #666;
        ">
          *Alle Rabatt-Aktionen gelten ausschließlich für die Kategorie Sale.
        </div>
      </div>
    `;
  },
};

document.addEventListener('DOMContentLoaded', () => cartDrawerTiers.init());
